import {
  DocumentIcon,
  DocumentPlusIcon,
  PhotoIcon,
} from "@heroicons/react/24/solid";
import { ClockIcon, TrashIcon } from "@heroicons/react/24/outline";
import { FC } from "react";
import { isExcel, isPdf, removeFullUrl } from "helpers/file";

interface Props {
  url: string;
  onDelete?: () => void;
  isInspectionPd?: boolean;
  name: string;
}
export const FilePreview: FC<Props> = ({ onDelete, url, name }) => {
  return (
    <div className="flex rounded-lg border border-gray-100 p-4 shadow-sm">
      <div className="flex items-center">
        {isPdf(name) ? (
          <DocumentIcon className="h-8 text-red-400" />
        ) : isExcel(name) ? (
          <DocumentPlusIcon className="h-8 text-green-600" />
        ) : (
          <PhotoIcon className="h-8 text-gray-400" />
        )}
        {isPdf(name) || isExcel(name) ? (
          <a
            href={url}
            download={name}
            className="ml-2 flex cursor-pointer flex-col space-y-1"
          >
            <span
              className="truncate text-sm font-semibold"
              style={{ maxWidth: "45ch" }}
            >
              {removeFullUrl(name)}
            </span>
            <span className="text-sm text-gray-500">
              {getFileTypeName(name)}
            </span>
          </a>
        ) : (
          <a
            href={`${name}`}
            target="_blank"
            rel="noreferrer"
            className="ml-2 flex cursor-pointer flex-col space-y-1"
          >
            <span
              className="truncate text-sm font-semibold"
              style={{ maxWidth: "45ch" }}
            >
              {removeFullUrl(name)}
            </span>
            <span className="text-sm text-gray-500">Bild</span>
          </a>
        )}
      </div>
      {!!onDelete && (
        <div className="ml-auto flex items-center pl-3">
          <TrashIcon
            onClick={onDelete}
            className="h-4 cursor-pointer text-sm text-gray-500 hover:scale-110 hover:transform hover:text-red-400"
          />
        </div>
      )}
    </div>
  );
};

const getFileTypeName = (name: string) => {
  if (isPdf(name)) {
    return "PDF";
  } else if (isExcel(name)) {
    return "Excel";
  } else {
    return "";
  }
};

interface InspectionPDFPreviewProps {
  onClick: () => void;
  inspectionName: string;
  isLoading?: boolean;
}
export const InspectionPDFPreview: FC<InspectionPDFPreviewProps> = ({
  onClick,
  inspectionName,
  isLoading = false,
}) => {
  return (
    <div
      className="relative flex rounded-lg border border-gray-100 p-4 shadow-sm"
      onClick={onClick}
    >
      <div className="flex items-center">
        <DocumentIcon className="h-8 text-red-400" />
        <div className="ml-2 flex cursor-pointer flex-col space-y-1">
          <span
            className="truncate text-sm font-semibold"
            style={{ maxWidth: "45ch" }}
          >
            {inspectionName}
          </span>
          <span className="text-sm text-gray-500">PDF</span>
        </div>
        {isLoading && <Loader />}
      </div>
    </div>
  );
};

interface PendingPDFCardProps {
  title: string;
  subtitle: string;
}
/** Platshållare för en PDF som genereras först när flödet är slutfört. */
export const PendingPDFCard: FC<PendingPDFCardProps> = ({
  title,
  subtitle,
}) => (
  <div className="flex items-center gap-4 rounded-lg border border-dashed border-gray-300 p-4">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100">
      <ClockIcon className="h-5 w-5 text-gray-500" />
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
  </div>
);

const Loader = () => (
  <svg
    className="absolute right-2 h-7 w-7 animate-spin text-cyan-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);
